/**
 * This file defines the fireErrorAnalytics() service.
 */
import type { ErrorInfo } from 'react';

import {
	OPERATIONAL_EVENT_TYPE,
	type GasCorePayload,
	type GasPurePayload,
} from '@atlaskit/analytics-gas-types';
import type { CreateUIAnalyticsEvent } from '@atlaskit/analytics-next/types';
import type UIAnalyticsEvent from '@atlaskit/analytics-next/UIAnalyticsEvent';
import { fg } from '@atlaskit/platform-feature-flags/fg';

import type {
	AbstractServiceMarker,
	AnyServiceWithDeps,
	DependenciesContext,
} from '../../core/types';

import { createAnalyticsEvent, getAnalyticsChannel, type EnablementTouchpoint } from './common';

export type AnalyticsMeta = {
	id: string;
	packageName?: string;
	teamName?: string;
};

export type AnalyticsAttributes = {
	[key: string]:
		| boolean
		| string
		| number
		| AnalyticsAttributes
		| AnalyticsAttributes[]
		| undefined
		| null;
};

export type AnalyticsPayload = {
	attributes?: AnalyticsAttributes;
	error?: Error;
	errorInfo?: ErrorInfo;
	event?: UIAnalyticsEvent;
	meta: AnalyticsMeta;
	sendToPrivacyUnsafeSplunk?: boolean;
	skipSentry?: boolean;
};

type FireErrorAnalyticsDeps = {
	createAnalyticsEvent: CreateUIAnalyticsEvent & AbstractServiceMarker;
	getAnalyticsChannel?: typeof getAnalyticsChannel;
};

const fireErrorAnalyticsDependencies: FireErrorAnalyticsDeps = {
	createAnalyticsEvent,
	getAnalyticsChannel,
};

export const fireErrorAnalytics: typeof fireErrorAnalyticsImpl & {
	dependencies: FireErrorAnalyticsDeps;
} = Object.assign(fireErrorAnalyticsImpl, {
	dependencies: fireErrorAnalyticsDependencies,
}) satisfies AnyServiceWithDeps<FireErrorAnalyticsDeps>;

// Still allows an async override of fireErrorAnalytics, but the default implementation is synchronous.
function fireErrorAnalyticsImpl(
	input: AnalyticsPayload,
	context: DependenciesContext<FireErrorAnalyticsDeps>,
): void {
	const touchpoint = input.attributes?.touchpoint as EnablementTouchpoint | undefined;
	if (fg('expansion_services_invite_in_core_invites')) {
		fireErrorAnalyticsOnChannel(input, touchpoint, context);
		return;
	}
	context
		.dependencies()
		.createAnalyticsEvent({
			type: 'sendOperationalEvent',
			data: {
				actionSubject: input.meta.id,
				action: 'failed',
				...(touchpoint ? { source: touchpoint } : {}),
				attributes: {
					...input.attributes,
					...(input.error && { message: input.error.message }),
				},
				tags: ['growth'],
			} satisfies GasPurePayload,
		})
		.fire();
}

function fireErrorAnalyticsOnChannel(
	input: AnalyticsPayload,
	touchpoint: EnablementTouchpoint | undefined,
	context: DependenciesContext<FireErrorAnalyticsDeps>,
): void {
	const deps = context.dependencies();
	deps
		.createAnalyticsEvent({
			eventType: OPERATIONAL_EVENT_TYPE,
			actionSubject: input.meta.id,
			action: 'failed',
			...(touchpoint ? { source: touchpoint } : {}),
			attributes: {
				...input.attributes,
				...(input.error && { message: input.error.message }),
			},
			tags: ['growth'],
		} satisfies GasCorePayload)
		.fire(deps.getAnalyticsChannel?.());
}
